/**
 * Post-claim welcome flow (IX-3929).
 *
 * After the wizard atomically claims a domain, the user passes through a short
 * personalization Loader and a Plans tier-picker before landing in the app. The
 * two `/welcome/*` screens are gated by a `sessionStorage` handoff written at
 * claim time: it carries the just-entered company / domain / brand tint (so the
 * Loader can greet + morph without re-querying), plus the exact durable
 * ingestion-operation ID when the control-plane producer is enabled. It also
 * doubles as the gate — visit `/welcome/*` without it and you are redirected
 * home, so the flow can't be deep-linked after the fact.
 */
import type { OnboardingClaimPayload } from '@/services/onboarding';

const WELCOME_KEY = 'rose_welcome';

export interface WelcomeKnowledgeOperation {
    operationId: string;
}

export interface WelcomePayload {
    company: string;
    domain: string;
    /** Detected site primary colour the Loader morphs the pixel field toward. */
    tint?: string;
    /** Present only when onboarding created a durable control-plane operation. */
    knowledgeOperation?: WelcomeKnowledgeOperation;
    /** The loader is still submitting the claim and must remain reachable pre-provisioning. */
    claimPending?: boolean;
}

/** Ephemeral navigation state used to submit the claim from the welcome loader. */
export interface WelcomeLoaderState {
    company: string;
    tint?: string;
    pendingClaim: {
        email: string;
        request: OnboardingClaimPayload;
    };
}

export function setWelcome(payload: WelcomePayload): void {
    try {
        sessionStorage.setItem(WELCOME_KEY, JSON.stringify(payload));
    } catch {
        // Storage unavailable (private mode) — flow degrades to a direct landing.
    }
}

export function getWelcome(): WelcomePayload | null {
    try {
        const raw = sessionStorage.getItem(WELCOME_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<WelcomePayload>;
        if (typeof parsed !== 'object' || parsed === null) return null;
        const operation = parsed.knowledgeOperation;
        const knowledgeOperation =
            operation &&
            typeof operation === 'object' &&
            typeof operation.operationId === 'string' &&
            operation.operationId.trim()
                ? {
                      operationId: operation.operationId.trim(),
                  }
                : undefined;
        return {
            company: typeof parsed.company === 'string' ? parsed.company : '',
            domain: typeof parsed.domain === 'string' ? parsed.domain : '',
            tint: typeof parsed.tint === 'string' ? parsed.tint : undefined,
            knowledgeOperation,
            claimPending: parsed.claimPending === true,
        };
    } catch {
        return null;
    }
}

export function clearWelcome(): void {
    try {
        sessionStorage.removeItem(WELCOME_KEY);
    } catch {
        // Ignore — worst case the flag lingers for this tab until it closes.
    }
}
